import type { Identifiable, Nullable, UUID } from '../../lib/typeHelpers.no-deps'
import type { SupportedLanguage } from 'zaptic-types'
import type { Dictionary } from '../../lib/functional/dictionary.no-deps'

import Fuse from 'fuse.js'

export type TUnit = TSimpleUnit & {
    children: number[]
    parents: number[]

    // Keeping this as optional for now, can be updated to be non optional if required
    experts?: number[]
    typeName?: string
}

export type TSimpleUnit = {
    name: string
    type: number
    data: { image?: string } & { [key: string]: any }
    foreignIds: any
    version: UUID
}

export type EditableUnit = Identifiable<Partial<TUnit>>
export type AddableUnit = Omit<TUnit, 'version'>

export type TFullUnit = Identifiable<TUnit & { lastMod: Date; isDeleted: boolean }>

export type KnownUnit = { kind: 'KnownUnit' } & TFullUnit

export type TUnitDisplayValuesFormat = {
    data: TUnitDisplayValue[]
    foreignIds: TUnitDisplayValue[]
    unitListItem: TUnitDisplayValue[]
}

export type TUnitDisplayValue = {
    name?: string
    key: string
    ordering: number
    style?: any
}

export type TUnitLookup = {
    type: 'barcode' | 'code-lookup'
    target: 'foreignIds' | 'data'
    keyName: string
}

export type Options = {
    lookup?: TUnitLookup[]
}

export type Display = { icon?: string; colour?: string; displayValues?: TUnitDisplayValuesFormat }

export type UnitTypeBase = {
    name: string
    display: Display
    options: Options
}

export type UnitTypeWithUnitCount = Identifiable<UnitTypeBase & { unitCount: number; isDeleted: boolean }>

export type UnitTypeWithLastMod = UnitTypeBase & { lastMod: Date }

export type TFullUnitType = UnitTypeWithLastMod & {
    children: number[]
    parents: number[]
}

export type AddableUnitType = UnitTypeBase & {
    children: number[]
    parents: number[]
}

export type AppUnitType = Identifiable<AddableUnitType>

export interface GetAllWithNamesOptions {
    from?: Date
    until?: Date
    types?: number | number[]
    unitIds?: number | number[]
    lang?: SupportedLanguage
}

export interface GetAllWithLineageOptions {
    lang?: SupportedLanguage
}

export interface GetAllFilter {
    unitIds?: number[]
    unitTypes?: number[]
}

export interface DatabaseUnit {
    id: number
    type: number
    name: string
    data: any
    foreignIds: any
    isDeleted: boolean
    lastMod: Date
    version: UUID
    expiryDate?: Nullable<Date>
}
export interface DatabaseUnitType {
    id: number
    name: string
    display: any
    options: any
    isDeleted: boolean
    lastMod: Date
}

export interface DatabaseUnitRelation {
    parent: number
    child: number
}

export interface DatabaseUnitTypeRelation {
    parent: number
    child: number
}

export interface UnitVersion {
    id: UUID
    unit: number
    data: any
    publishedAt: Date
    publishedBy: Nullable<number>
    publishedComment: Nullable<string>
}

export interface UnitAttribute {
    unitTypeId: number
    name: string
    value?: string
}

export function fuzzyFilter(unitAttributes: UnitAttribute[], search: string): UnitAttribute[] {
    if (search === '') {
        return unitAttributes
    }
    const fuse = new Fuse(unitAttributes, { keys: ['name'], threshold: 0.3, ignoreLocation: true })
    return fuse.search(search).map((ua) => ua.item)
}

export type UnitData = Dictionary<string | number>
export type UnitListItem = {
    id: number
    startDate?: Nullable<Date>
    endDate?: Nullable<Date>
    ordering?: number
    displayValues?: any
}
type DatabaseUnitListItem = {
    id: number
    startDate?: string
    endDate?: string
    ordering?: number
    displayValues?: any
}
type DatabaseUnitList = {
    name: string
    type: string
    unitType: number
    data: any
    units: DatabaseUnitListItem[]
}
export type DatabaseSyncUnitList = Identifiable<{ locations: number[] } & DatabaseUnitList>
export type FullDatabaseUnitList = { lastMod: Date } & DatabaseSyncUnitList
export type FullDatabaseUnitListV2 = Identifiable<
    {
        locationLists: number[]
        lastMod: Date
    } & DatabaseUnitList
>
export type TUnitList = {
    name: string
    type: string
    unitType: number
    data: any
    units: UnitListItem[]
}
export type SyncUnitList = Identifiable<{ locations: number[] } & TUnitList>
export type TFullUnitList = { lastMod: Date } & SyncUnitList
export type TFullUnitListV2 = Identifiable<{
    lastMod: Date
    locationLists: number[]
}> &
    TUnitList
export type UnitByCampaign = { campaign: number; ids: number[]; names: string[] }
export type EditableUnitType = Identifiable<Partial<AddableUnitType>>

export interface SetUnitTypeRelationInput {
    id: number
    children: number[]
    parents: number[]
}

export type RemoveResult =
    | { error: { units: number[] }; removed: false }
    | { error: { activityTemplates: number[] }; removed: false }
    | { removed: true }

export interface UnitTypeRelationships {
    id: number
    parents: number[]
    children: number[]
}

export interface UnitRelationships {
    id: number
    parents: number[]
    children: number[]
}

export type UnitToUpsert = Omit<TSimpleUnit, 'version'> & {
    id: number | null
    expiryDate?: Date
    parents?: number[]
}

export type UnitSearchResult = Pick<TFullUnit, 'id' | 'name'>

export type UnitWithTypeAndParents = Pick<TFullUnit, 'id' | 'type' | 'parents'>
